import { useCallback, useEffect, useRef, useState } from 'react';
import { Command } from 'cmdk';
import { connect } from 'react-redux';
import { debounce } from 'lodash';
import { GlobalSearchStyles } from './globalSearch.style';
import GlobalSearchButton from './globalSearchButton/globalSearchButton';
import GlobalSearchInput from './globalSearchInput/globalSearchInput';
import GlobalSearchResults from './globalSearchResults/globalSearchResults';
import GlobalSearchFooter from './globalSearchFooter/globalSearchFooter';
import actions from './actions';

const GlobalSearch = ({ data, search, clearStore, org, getRecent, clickItem, collapsed }) => {
  const [isGlobalSearchVisible, setIsGlobalSearchVisible] = useState(false);
  const listRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastQuery, setLastQuery] = useState('');

  const { results, isSearchLoading, recent, isLoadMoreLoading, resultsTotalCount } = data;

  const toggleGlobalSearchVisibility = () => {
    setIsGlobalSearchVisible(visible => !visible);
  };

  useEffect(() => {
    const down = e => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        toggleGlobalSearchVisibility();
      }
    };

    document.addEventListener('keydown', down);

    return () => document.removeEventListener('keydown', down);
  }, []);

  useEffect(() => {
    if (!isGlobalSearchVisible) {
      clearStore();
      setLastQuery('');
      getRecent();
    }
  }, [isGlobalSearchVisible]);

  const handleSearch = useCallback(
    query => {
      setLastQuery(query);

      if (listRef.current) {
        listRef.current.scrollTop = 0;
      }

      setCurrentPage(1);

      if (!query || query.trim() === '') {
        clearStore();
      } else {
        search({
          query,
          page: currentPage,
          pageSize: 100,
        });
      }
    },
    [search, listRef, clearStore],
  );

  const onClear = () => {
    setCurrentPage(1);
    setLastQuery('');
    clearStore();
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onChangeDebounce = useCallback(
    debounce(query => {
      if (query === '') {
        setCurrentPage(1);
      }

      handleSearch(query);
    }, 500),
    [handleSearch],
  );

  return (
    <>
      <GlobalSearchStyles />
      <GlobalSearchButton toggleGlobalSearchVisibility={toggleGlobalSearchVisibility} collapsed={collapsed} />
      <Command.Dialog
        open={isGlobalSearchVisible}
        onOpenChange={toggleGlobalSearchVisibility}
        className="global-search-modal"
        overlayClassName="global-search-modal-overlay"
      >
        <Command shouldFilter={false} loop>
          <GlobalSearchInput onValueChange={onChangeDebounce} org={org} lastQuery={lastQuery} onClear={onClear} />
          <hr className="search-divider" />
          <GlobalSearchResults
            listRef={listRef}
            onScroll={e => {
              const scrollThreshold = 50;
              const isNearBottom =
                e.target.scrollHeight - e.target.scrollTop - e.target.clientHeight <= scrollThreshold;
              const loadedItemsCount = results?.length || 0;
              const hasMoreResults = loadedItemsCount < resultsTotalCount;

              if (isNearBottom && results !== null && !isLoadMoreLoading && hasMoreResults) {
                const page = currentPage + 1;
                setCurrentPage(page);

                search({ query: lastQuery, page, pageSize: 100 });
              }
            }}
            isSearchLoading={isSearchLoading}
            isLoadMoreLoading={isLoadMoreLoading}
            results={results}
            recent={recent}
            toggleGlobalSearchVisibility={toggleGlobalSearchVisibility}
            clickItem={clickItem}
            query={lastQuery}
          />
          <GlobalSearchFooter />
        </Command>
      </Command.Dialog>
    </>
  );
};

export default connect(
  state => ({
    data: state.GlobalSearch,
    org: state.StartAppSettings?.organization?.name,
  }),
  dispatch => ({
    search: data => dispatch(actions.searchRequest(data)),
    clearStore: () => dispatch(actions.clearStore()),
    getRecent: () => dispatch(actions.getRecentRequest()),
    clickItem: data => dispatch(actions.clickItemRequest(data)),
  }),
)(GlobalSearch);
